/* eslint-disable react/destructuring-assignment */
import { Children, forwardRef, isValidElement, useMemo } from 'react'
import styled, { css } from 'styled-components'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { Typo } from '@/components/common/typography'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { IconSizeEnum } from '@/constants/icon-size.enum'
import { FIXED_HEIGHT } from '@/constants/legacy/constLayout'

const BASE_FONT_SIZE = 10

const LIMITED_ICON_COUNT = 3

type TopNavUiType = 'icon' | 'icon-many' | 'text'

/**
 * `TopNav` UI 컴포넌트
 */
export const TopNav = forwardRef<HTMLHeadElement, React.ComponentPropsWithoutRef<'header'>>(
  ({ children, ...rest }, ref) => {
    const type = useMemo(() => getTopNavUiType(children), [children])

    return (
      <StyledTopNav ref={ref} type={type} {...rest}>
        {children}
      </StyledTopNav>
    )
  }
)

TopNav.displayName = 'TopNav'

/**
 * TopNav의 좌측 영역을 그리는 UI 컴포넌트
 */
export const TopNavLeftArea = ({ children, ...rest }: React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <StyledTopNavArea direction="left" {...rest}>
      {children}
    </StyledTopNavArea>
  )
}

/**
 * TopNav의 우측 영역을 그리는 UI 컴포넌트
 */
export const TopNavRightArea = ({ children, ...rest }: React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <StyledTopNavArea direction="right" {...rest}>
      {children}
    </StyledTopNavArea>
  )
}

/**
 * `TopNav`에서 사용하는 Title 컴포넌트 UI
 */
export const TopNavTitle = ({ children, className }: React.ComponentPropsWithoutRef<'span'>) => {
  return (
    <Typo
      as="span"
      variant="Heading6 Bold"
      color={GlobalColorEnum.Gray900}
      className={className}
      style={{
        flexShrink: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {children}
    </Typo>
  )
}

type TopNavIconButtonProps =
  | (Omit<React.ComponentPropsWithoutRef<'button'>, 'type' | 'color'> & {
      /** 커스텀한 자식 요소 적용 여부 */
      asChild: true
    })
  | (Omit<React.ComponentPropsWithoutRef<'button'>, 'type' | 'color'> &
      Omit<React.ComponentProps<typeof FlatIcon>, 'width'> & {
        /** 커스텀한 자식 요소 적용 여부 */
        asChild?: false
      })

/**
 * `TopNav`에서 사용하는 Icon 버튼 컴포넌트 UI
 */
export const TopNavIconButton = (props: TopNavIconButtonProps) => {
  if (props.asChild) {
    const { children, ...rest } = props

    return (
      <StyledTopNavIconButton type="button" {...rest}>
        {children}
      </StyledTopNavIconButton>
    )
  }

  const { type, size = IconSizeEnum.Size24, color = GlobalColorEnum.Gray900, ...rest } = props

  return (
    <StyledTopNavIconButton type="button" {...rest}>
      <FlatIcon type={type} size={size} color={color} />
    </StyledTopNavIconButton>
  )
}

/**
 * `TopNav`에서 사용하는 Text 버튼 컴포넌트 UI
 */
export const TopNavTextButton = ({ children, ...rest }: React.ComponentPropsWithoutRef<'button'>) => {
  return (
    <StyledTopNavTextButton type="button" {...rest}>
      <Typo as="div" variant="Body1 Medium" color={GlobalColorEnum.Gray800}>
        {children}
      </Typo>
    </StyledTopNavTextButton>
  )
}

const StyledTopNav = styled.header<{ type: TopNavUiType }>`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  padding: 0rem 1.6rem;
  background-color: ${({ theme }) => theme.color.grayWhite};
  position: sticky;
  top: 0;
  z-index: 4;
  width: 100%;
  height: ${FIXED_HEIGHT.HEADER / BASE_FONT_SIZE}rem;
  border-bottom: ${(props) => `0.1rem solid ${props.theme.color.gray50}`};

  ${({ type }) =>
    type === 'icon' &&
    css`
      > span {
        width: calc(2.4rem * 4 + 1.6rem * 4);
        text-align: center;
      }
    `}
  ${({ type }) =>
    type === 'icon-many' &&
    css`
      > :first-child {
        width: auto;
      }

      > span {
        width: calc(2.4rem * 4 + 1.6rem * 4);
        text-align: left;
      }
    `}
    ${({ type }) =>
    type === 'text' &&
    css`
      > span {
        width: calc(2.4rem * 4 + 1.6rem * 2);
        text-align: center;
      }
    `}
`

const StyledTopNavArea = styled.div<{ direction: 'left' | 'right' }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1.6rem;
  justify-content: ${({ direction }) => (direction === 'left' ? 'flex-start' : 'flex-end')};
`

const StyledTopNavIconButton = styled.button`
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  border: 1px solid transparent;
  background-color: transparent;
  cursor: pointer;
`

const StyledTopNavTextButton = styled.button`
  display: flex;
  align-items: center;
  height: auto;
  padding: 0;
  border: 1px solid transparent;
  background-color: transparent;
  cursor: pointer;
`

const getTopNavUiType = (children: React.ReactNode): TopNavUiType => {
  const validChildren = Children.toArray(children).filter(isValidElement)

  const hasTopNavTextButtonInChildren = validChildren.some((child: React.ReactElement) => {
    if (child.type === TopNavRightArea) {
      return Children.toArray(child.props.children)
        .filter(isValidElement)
        .some((grandChild: React.ReactElement) => grandChild.type === TopNavTextButton)
    }
  })

  if (hasTopNavTextButtonInChildren) {
    return 'text'
  }

  const countTopNavIconButtonsInChildren = validChildren.reduce((count, child: React.ReactElement) => {
    if (child.type === TopNavRightArea) {
      return count + Children.toArray(child.props.children).filter(isValidElement).length
    }
    return count
  }, 0)

  if (countTopNavIconButtonsInChildren >= LIMITED_ICON_COUNT) {
    return 'icon-many'
  }

  return 'icon'
}
